import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = {
  for: "users",
  class: "mb-2 block text-sm font-medium text-gray-800"
}
const _hoisted_4 = { class: "flex flex-col justify-end pb-1.5" }

import { computed, reactive, ref, onMounted, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate, type Validation, type ValidationArgs } from '@vuelidate/core';

import { required, maxLength } from '@/utils/validators';
import { goBack } from '@/composables/useNavigation';
import { useUiStore } from '@/stores';
import { UserLabelService, SelectOptionsService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import {
    FormAction,
    type UserLabelModel,
    type UserLabelFormState,
    Permission,
    SelectOptionsServiceGetUsersResult,
} from '@/types';
import type { AxiosResponse } from 'axios';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserLabelFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const props = __props;

const route = useRoute();

const uiStore = useUiStore();

const formState = reactive<UserLabelFormState>({
    label: null,
    users: [],
    isActive: true,
});

const rules = computed<ValidationArgs<UserLabelFormState>>(() => ({
    label: { required, maxLength: maxLength(50) },
    users: {},
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<UserLabelFormState>>> = useVuelidate(rules, formState);

const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getUsers = async () => {
    try {
        const response = await SelectOptionsService.getUsers();
        usersOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchUserLabel = async (id: number) => {
    setLoading(true);
    try {
        const { data } = await UserLabelService.get<UserLabelModel>(id);
        formState.label = data.label;
        formState.users = data.users.map((u) => u.id);
        formState.isActive = data.isActive;
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

const onSubmit = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    setLoading(true);

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewUserLabelById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await UserLabelService.update(id, formState);
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await UserLabelService.create(formState);
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await getUsers();
    if (props.action === FormAction.EDIT) {
        await fetchUserLabel(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('userLabel.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListUserLabel' } }),
          (
                            (props.action == _unref(FormAction).CREATE &&
                                _ctx.$can(_unref(Permission).CREATE_USER_LABEL)) ||
                            (props.action == _unref(FormAction).EDIT && _ctx.$can(_unref(Permission).EDIT_USER_LABEL))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }, {
      default: _withCtx(() => [
        _createVNode(_component_UiTextInput, {
          modelValue: _unref(v$).label.$model,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).label.$model) = $event)),
          name: "label",
          type: "text",
          label: _ctx.$t('userLabel.labels.label'),
          errors: _unref(v$).label.$errors,
          onBlur: _unref(v$).label.$touch
        }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
        _createElementVNode("div", null, [
          _createElementVNode("label", _hoisted_3, _toDisplayString(_ctx.$t('userLabel.labels.users')), 1),
          _createVNode(_component_UiMultiselect, {
            id: "users",
            name: "users",
            label: "name",
            "value-prop": "id",
            modelValue: formState.users,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formState.users) = $event)),
            multiple: "",
            primitive: "",
            options: usersOptions.value,
            clearable: ""
          }, null, 8, ["modelValue", "options"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_UiCheckboxInput, {
            modelValue: _unref(v$).isActive.$model,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
            name: "isActive",
            label: _ctx.$t('userLabel.labels.isActive'),
            class: "h-6"
          }, null, 8, ["modelValue", "label"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})